import React, { useState } from "react";
import { L10n, L10nHtml } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { useStore } from "../../../models";
import { SubtitleDescription } from "../subtitleDescription";
import { IOrgConfig } from "../../../models/currentUser/OrgConfig";
import commonStyles from "../../../containers/settings/settingsEditCommon.css";
import { observer } from "mobx-react";
import styles from "./reportsSettings.css";
import { SettingsForm } from "../settingsForm";
import { SimpleCheckboxPlaceholder } from "../settingsForms/checkboxPlaceholder/simpleCheckboxPlaceholder";
import { MockedPerfomanceIndicator } from "../../dashboard/performanceIndicators/performanceIndicator";

interface Props {
  orgConfig: IOrgConfig;
}

export const MetricsPreviewSettings = observer(({ orgConfig }: Props) => {
  const [
    waitingConversationsMetricEnabled,
    openedConversationsMetricEnabled,
    averageResponseTimeMetricEnabled,
    onWaitingConversationMetricEnabledChanged,
    onOpenedConversationsMetricEnabled,
    onAverageResponseTimeMetricEnabled,
    submit,
    discard,
    isChanged
  ] = metricsPreviewSettingsState(orgConfig);

  return (
    <>
      <div className={commonStyles.title}>
        <L10n id={"settings.reports.title"} />
      </div>

      <div className={commonStyles.section}>
        <SubtitleDescription
          subtitle={intl.formatMessage("settings.reports.preview")}
          description={intl.formatMessage("settings.reports.description")}
        />
        <SettingsForm disabled={!isChanged} onSubmit={submit} onDiscard={discard}>
          <div className={commonStyles.content}>
            <div className={styles["preview-checkboxes-container"]}>
              <SimpleCheckboxPlaceholder
                value={averageResponseTimeMetricEnabled}
                id={"average_reponse_time_metric_enabled"}
                labelId={"settings.reports.display"}
                onChange={onAverageResponseTimeMetricEnabled}
              >
                <div>
                  <MockedPerfomanceIndicator
                    iconClassName="icon-first-message-time"
                    data={"00m 00s"}
                  />
                  <p className={styles.description}>
                    <L10nHtml id="reports.averageResponseTime.tooltip" />
                  </p>
                </div>
              </SimpleCheckboxPlaceholder>

              <SimpleCheckboxPlaceholder
                value={waitingConversationsMetricEnabled}
                id={"waiting_conversation_metric_enabled"}
                labelId={"settings.reports.display"}
                onChange={onWaitingConversationMetricEnabledChanged}
              >
                <div>
                  <MockedPerfomanceIndicator iconClassName="icon-person-bold" data={"0"} />
                  <p className={styles.description}>
                    <L10nHtml id="reports.waitingQueue.tooltip" />
                  </p>
                </div>
              </SimpleCheckboxPlaceholder>

              <SimpleCheckboxPlaceholder
                value={openedConversationsMetricEnabled}
                id={"opened_conversation_metric_enabled"}
                labelId={"settings.reports.display"}
                onChange={onOpenedConversationsMetricEnabled}
              >
                <div>
                  <MockedPerfomanceIndicator iconClassName="icon-active-conversations" data={"0"} />
                  <p className={styles.description}>
                    <L10nHtml id="reports.yoursConversations.tooltip" />
                  </p>
                </div>
              </SimpleCheckboxPlaceholder>
            </div>
          </div>
        </SettingsForm>
      </div>
    </>
  );
});

type booleanCallback = (value: boolean) => void;

const metricsPreviewSettingsState = (
  orgConfig: IOrgConfig
): [
  boolean,
  boolean,
  boolean,
  booleanCallback,
  booleanCallback,
  booleanCallback,
  () => void,
  () => void,
  boolean
] => {
  const { uiStore } = useStore();
  const [isChanged, setIsChanged] = useState<boolean>(false);
  const [waitingConversationsMetricEnabled, setWaitingConversationsMetricEnabled] =
    useState<boolean>(orgConfig.waitingConversationsMetricEnabled);

  const [openedConversationsMetricEnabled, setOpenedConversationsMetricEnabled] = useState<boolean>(
    orgConfig.openedConversationsMetricEnabled
  );
  const [averageResponseTimeMetricEnabled, setAverageResponseTimeMetricEnabled] = useState<boolean>(
    orgConfig.averageResponseTimeMetricEnabled
  );

  const submit = async () => {
    try {
      await orgConfig.update({
        waitingConversationsMetricEnabled,
        openedConversationsMetricEnabled,
        averageResponseTimeMetricEnabled
      });
      setIsChanged(false);
    } catch (e) {
      uiStore.addErrorToast(intl.formatMessage("error.something"));
    }
  };

  const onWaitingConversationMetricEnabledChanged = (checked: boolean) => {
    setWaitingConversationsMetricEnabled(checked);
    setIsChanged(true);
  };

  const onOpenedConversationsMetricEnabled = (checked: boolean) => {
    setOpenedConversationsMetricEnabled(checked);
    setIsChanged(true);
  };

  const onAverageResponseTimeMetricEnabled = (checked: boolean) => {
    setAverageResponseTimeMetricEnabled(checked);
    setIsChanged(true);
  };

  const discard = () => {
    setWaitingConversationsMetricEnabled(orgConfig.waitingConversationsMetricEnabled);
    setOpenedConversationsMetricEnabled(orgConfig.openedConversationsMetricEnabled);
    setAverageResponseTimeMetricEnabled(orgConfig.averageResponseTimeMetricEnabled);
    setIsChanged(false);
  };

  return [
    waitingConversationsMetricEnabled,
    openedConversationsMetricEnabled,
    averageResponseTimeMetricEnabled,
    onWaitingConversationMetricEnabledChanged,
    onOpenedConversationsMetricEnabled,
    onAverageResponseTimeMetricEnabled,
    submit,
    discard,
    isChanged
  ];
};
